/**
 * Panel Shell — the one mount point every suite application uses.
 *
 * Owns the cross-instance state the panel chrome shares (floating windows,
 * the dockable panel instance list, add/remove handlers) and the providers
 * the shell chrome consumes (label resolver, context menu). The application
 * composes its workspace from dock strips and its own surface:
 *
 *   <PanelShell registry={registry} resolveLabel={t}>
 *     <PanelDockStrip side="left" />
 *     <WorkspaceSurface />
 *     <PanelDockStrip side="right" />
 *   </PanelShell>
 *
 * One PanelShell per document: the drag state, dock rect registry and
 * visibility flags behind it are module singletons.
 */

import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { PanelLabelProvider, LabelResolver } from '../primitives/labels';
import { ContextMenuProvider } from '../primitives/ContextMenu';
import { PanelRegistry } from '../registry/createPanelRegistry';
import { useSidePanelStore } from '../store/useSidePanelStore';
import { useDockLayoutStore, DockSide } from '../store/useDockLayoutStore';
import type { FloatingWindow, TabConfig } from './types';
import { PANEL_EVENTS } from './protocol';
import { GlobalDragGhost } from './GlobalDragGhost';
import '../theme/side-panel.css';
import '../theme/floating-panel.css';
import '../theme/minimized-sidepanel.css';
import '../theme/toolbar.css';

export const TOOLBAR_DOCK_ENTRY_ID = 'toolbar';
export const DEFAULT_PANEL_INSTANCE_ID = 'main-panel';

export interface PanelShellContextValue {
    registry: PanelRegistry;
    /** Panel instance ids currently mounted (dynamic instances included) */
    panels: string[];
    /** The app's toolbar node (null while suite chrome is hidden) */
    toolbarSlot: React.ReactNode;
    /** Spawn a panel instance on an edge; returns the new instance id */
    addPanel: (
        side: DockSide,
        panelId?: string,
        tab?: TabConfig,
    ) => string;
    removePanel: (panelId: string) => void;
    floatingWindows: FloatingWindow[];
    setFloatingWindows: React.Dispatch<React.SetStateAction<FloatingWindow[]>>;
}

const PanelShellContext = React.createContext<PanelShellContextValue | null>(
    null,
);

export const usePanelShell = (): PanelShellContextValue => {
    const ctx = React.useContext(PanelShellContext);
    if (!ctx) {
        throw new Error('usePanelShell must be used within <PanelShell>');
    }
    return ctx;
};

export interface PanelShellProps {
    registry: PanelRegistry;
    /** Maps label keys to display text (the app's `t`); defaults to the
        English fallbacks the registry declares. */
    resolveLabel?: LabelResolver;
    /** The app's toolbar, rendered in the fixed 'toolbar' dock slot. */
    toolbar?: React.ReactNode;
    /** Panel instance ids present at mount. Workbenches that render their
        own instance set pass it explicitly; the default keeps the legacy
        primary 'main-panel' shell so existing mounts are unchanged. */
    initialPanels?: string[];
    /** Hidden while suite chrome is hidden (embedded modes) */
    chromeHidden?: boolean;
    children?: React.ReactNode;
}

let dynamicPanelCounter = 0;

export const PanelShell: React.FC<PanelShellProps> = ({
    registry,
    resolveLabel,
    toolbar,
    initialPanels,
    chromeHidden = false,
    children,
}) => {
    // The registry locks the storage namespace at creation (module scope,
    // before any store boots); nothing to configure at mount time.
    const [panels, setPanels] = useState<string[]>(
        initialPanels ?? [DEFAULT_PANEL_INSTANCE_ID],
    );
    const [floatingWindows, setFloatingWindows] = useState<FloatingWindow[]>(
        [],
    );

    const addPanel = useCallback(
        (side: DockSide, panelId?: string, _tab?: TabConfig): string => {
            const id =
                panelId ??
                `panel-${Date.now()}-${dynamicPanelCounter++}`.slice(0, 40);
            setPanels((prev) =>
                prev.includes(id) ? prev : [...prev, id],
            );
            const { strips } = useDockLayoutStore.getState();
            useDockLayoutStore.getState().dockEntry(id, {
                side,
                index: strips[side].length,
            });
            return id;
        },
        [],
    );

    const removePanel = useCallback((panelId: string) => {
        setPanels((prev) => prev.filter((id) => id !== panelId));
        useDockLayoutStore.getState().removeEntry(panelId);
    }, []);

    // A panel closed from its own chrome (SidePanelCore's remove path inside
    // a strip entry) also needs to leave the shell's instance list.
    useEffect(() => {
        const handlePanelClosed = (e: Event) => {
            const detail = (e as CustomEvent<{ panelId?: string }>).detail;
            if (!detail?.panelId) return;
            setPanels((prev) => prev.filter((id) => id !== detail.panelId));
        };
        window.addEventListener(PANEL_EVENTS.panelClosed, handlePanelClosed);
        return () => {
            window.removeEventListener(PANEL_EVENTS.panelClosed, handlePanelClosed);
        };
    }, []);

    const contextValue = useMemo<PanelShellContextValue>(
        () => ({
            registry,
            panels,
            toolbarSlot: chromeHidden ? null : toolbar ?? null,
            addPanel,
            removePanel,
            floatingWindows,
            setFloatingWindows,
        }),
        [registry, panels, chromeHidden, toolbar, addPanel, removePanel, floatingWindows],
    );

    return (
        <PanelLabelProvider resolveLabel={resolveLabel}>
            <ContextMenuProvider>
                <PanelShellContext.Provider value={contextValue}>
                    {children}
                    <GlobalDragGhost />
                </PanelShellContext.Provider>
            </ContextMenuProvider>
        </PanelLabelProvider>
    );
};

export { useSidePanelStore, useDockLayoutStore };
